<template>
  <div class="relative">
    <div
        v-bind="getRootProps()"
        class="w-full text-center border-dashed border border-secondary-500 rounded-md p-[20px] flex flex-col justify-center items-center"
        :class="[files.length === 0 ? 'cursor-pointer' : '', isLoading ? 'animate-pulse' : '']"
    >
      <div v-if="files.length === 0">
        <input v-bind="getInputProps()" class="hidden"/>
        <img src="@/assets/images/svg/upload.svg" alt="" class="mx-auto mb-4"/>
        <p
            v-if="isDragActive"
            class="text-sm text-slate-500 dark:text-slate-300 font-light"
        >
          Drop the files here ...
        </p>
        <p v-else class="text-sm text-slate-500 dark:text-slate-300 font-light">
          {{ desc }}
        </p>
      </div>
      <div v-if="!isLoading" class="flex space-x-4 w-full h-full">
        <div v-for="(file, i) in files" :key="i" class="flex-none w-full h-full">
          <div class="h-[200px] w-full mx-auto rounded-md" key="{i}">
            <iframe v-if="file.type === 'pdf'" :src="file.preview" class="h-full w-full"></iframe>
            <img
                v-else
                :src="file.preview"
                :alt="file.name"
                class="object-contain h-full w-full block rounded-md"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="!isLoading" class="absolute flex flex-row gap-2 top-4 right-4">
      <Tooltip v-if="isUploaded === true"
               placement="top"
               btnClass="border border-white border-solid p-2 rounded-circile bg-green-600"
               arrow
      >
        <template v-slot:button>
          <div @click.prevent class="border border-white border-solid p-2 rounded-circile bg-green-600">
            <Icon icon="icon-park-outline:link-cloud-sucess" height="20"
                  class="cursor-pointer text-white"/>
          </div>
        </template>
        <template v-slot:default>
          {{ $t('message.uploaded_successfully') }}
        </template>
      </Tooltip>
      <Tooltip v-else-if="isUploaded ===  false"
               placement="top"
               arrow
      >
        <template v-slot:button>
          <div @click.prevent class="border border-white border-solid p-2 rounded-circile bg-red-600">
            <Icon icon="icon-park-outline:link-cloud-faild" height="20"
                  class="cursor-pointer text-white"/>
          </div>
        </template>
        <template v-slot:default>
          {{ $t('message.upload_failed') }}
        </template>
      </Tooltip>
      <Tooltip v-else-if="isUploaded === 'loading'"
               placement="top"
               arrow
      >
        <template v-slot:button>
          <div @click.prevent class="border border-white border-solid p-2 rounded-circile bg-blue-500">
            <Icon icon="line-md:uploading-loop" height="20"
                  class="cursor-pointer text-white"/>
          </div>
        </template>
        <template v-slot:default>
          {{ $t('message.uploading') }}
        </template>
      </Tooltip>
      <Tooltip v-if="files.length !== 0 && isUploaded === true"
               placement="top"
               arrow
      >
        <template v-slot:button>
          <button @click.prevent="removeImage"
                  class="border border-black-500 border-solid p-2 rounded-circile bg-slate-50">
            <Icon icon="fluent:delete-24-regular" height="20"
                  class="cursor-pointer dark:text-slate-800 hover:text-red-800 dark:hover:text-red-800"/>
          </button>
        </template>
        <template v-slot:default>
          {{ $t('message.click_to_remove') }}
        </template>
      </Tooltip>
    </div>
  </div>
</template>

<script>
import {useDropzone} from "vue3-dropzone";
import {ref, onMounted, onBeforeUnmount} from "vue";
import {Icon} from "@iconify/vue";
import Tooltip from "@/components/Tooltip/index.vue";
import {useUploadStore} from "@/stores/upload";

export default {
  name: "UseDropzoneDemo",
  components: {
    Icon,
    Tooltip,
  },
  props: {
    desc: {
      type: String,
      default: "Drop files here or click to upload.",
    },
    img: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
  },
  setup(props, {emit}) {
    const uploadStore = useUploadStore();
    const files = ref();
    const getFileExtension = (url) => {
      // Use regex to extract the extension
      const matches = url.match(/\.([a-z0-9]+)$/i);
      return matches ? matches[1] : 'unknown';
    };
    if (props.img) {
      files.value = [{preview: props.img, type: getFileExtension(props.img)}]
    } else {
      files.value = []
    }
    const isUploaded = ref('null')
    const url = "https://upload-tr.chainedminds.com/";
    const saveFiles = async (files) => {
      isUploaded.value = 'loading'
      emit('uploading', true)

      try {
        emit('fileSelected', files[0].name)

        const result = await uploadStore.uploadFile({
          fileName: files[0].name,
          file: files[0]
        }, {returnResponse: true})

        isUploaded.value = result?.success === true ? true : false

        if (isUploaded.value) {
          emit('uploaded', result.data.file.name)
        } else {
          emit('uploadFailed')
        }
      } catch (err) {
        isUploaded.value = false
        emit('uploadFailed')
        console.error('Upload failed:', err)
      } finally {
        emit('uploading', false)
      }
    };

    function onDrop(acceptFiles, rejectReasons) {
      files.value = acceptFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
      );
      //console.log(files.value)
      saveFiles(acceptFiles);
      console.log(rejectReasons);
    }

    const {getRootProps, getInputProps, ...rest} = useDropzone({
      //onDrop: onDrop,
      maxFiles: 1,
      multiple: props.multiple,
      onDropRejected: onDropRejected,
      onDropAccepted: onDropAccepted,
      accept: ['image/bmp', 'image/gif', 'image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'image/webp']
    });

    function onDropRejected(fileRejections) {
      console.log(fileRejections)
    }

    function onDropAccepted(acceptFiles) {
      files.value = acceptFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
      );
      saveFiles(acceptFiles);
    }

    function removeImage() {
      isUploaded.value = null
      files.value.splice(0, 1);
      emit('removed', true)
    }

    /*async function retry() {
      isUploaded.value = null
      const result = await uploadStore.uploadFile({
        fileName: files[0].name,
        file: files[0]
      }, {returnResponse: true})

      isUploaded.value = result?.success
    }*/

    const handlePaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const pastedFile = Object.assign(file, {
              preview: URL.createObjectURL(file)
            });
            files.value = [pastedFile];
            saveFiles([file]);
          }
        }
      }
    };

    onMounted(() => {
      window.addEventListener('paste', handlePaste);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('paste', handlePaste);
    });

    return {
      getRootProps,
      getInputProps,
      ...rest,
      files,
      isUploaded,
      removeImage,
      /*retry*/
    };
  },
};
</script>

<style scoped>
.delete-file-icon {
  top: calc(50% - 140px);
  right: calc(50% - 140px);
}
</style>
